/**
 * Canvas widget nodes ↔ DVM Layout Builder tiles.
 *
 * The Layout Builder's build steps and runtime helpers all work on
 * `DashboardTile`s. A Design Builder widget stores the tile it was generated
 * from as `widget.build` (minus render data) plus its render data as
 * `widget.data`, so a tile is rebuilt by putting the data back into the field
 * that family renders from. Tile ids are node ids.
 */

import { CLIENT_CHART_CROSS_FILTER, type DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { DesignDocument, DesignNode } from '../design-builder.types';

/** Where each family keeps its rendered data on a tile. */
const RENDER_FIELD: Record<string, keyof DashboardTile> = {
  WATERFALL: 'waterfallData',
  DRILL_DOWN_TABLE: 'drillData',
  NESTED_TABLE: 'nestedTableData',
  TABLE_MATRIX: 'tableMatrixData',
  HORIZONTAL_BAR_CHART: 'hbData',
  BAR: 'barData',
  LINE_AREA: 'lineAreaData',
  SCATTER_BUBBLE: 'scatterBubbleData',
  PIE_COMPOSITION: 'pieCompositionData',
  KPI: 'kpiData',
};

/** Rendered data never stored in `widget.build` (it lives in `widget.data`). */
const RENDER_KEYS: ReadonlyArray<keyof DashboardTile> = [
  ...Object.values(RENDER_FIELD), 'barRows', 'scatterBubbleRows', 'lineAreaRows', 'pieCompositionRows', 'previewOption', 'waterfallBarsAll',
];

/** Layout types that are input controls, never a recompute target. */
const INPUT_LAYOUTS = new Set(['SLIDER', 'INPUT_BOX', 'DROPDOWN', 'FILE_UPLOAD', 'POPUP', 'BUTTON']);

/**
 * Every chart widget as a compute target, read from the NODES rather than from
 * `tiles()`.
 *
 * A node only becomes a tile once `tileFromNode` can rebuild one, so a chart
 * missing its build — or one whose mapping lives in `binding` — dropped out of
 * the target list and the compute call went out with `targets: []`. The
 * componentId is what the backend matches on, and the widget carries it
 * directly.
 */
export function chartTargets(
  document: DesignDocument,
  excludeComponentId: string,
  field: string,
): Array<{ componentId: string; label: string; field: string }> {
  const charts = widgetNodes(document)
    .filter((node) => !INPUT_LAYOUTS.has(node.widget!.layoutType))
    .map((node) => {
      const widget = node.widget!;
      const build = widget.build ?? {};
      return {
        componentId: String(widget.componentId ?? build['componentId'] ?? ''),
        label: String(build['title'] ?? node.name ?? ''),
        field,
      };
    });
  // A text registered as a KPI component is a target like any chart.
  const texts = [...boundTextComponents(document)].map(([componentId, node]) => ({
    componentId,
    label: node.name ?? '',
    field,
  }));
  return [...charts, ...texts].filter(
    (target) => !!target.componentId && target.componentId !== excludeComponentId,
  );
}

/** Bound texts registered as components, keyed by componentId. */
export function boundTextComponents(document: DesignDocument): Map<string, DesignNode> {
  const out = new Map<string, DesignNode>();
  const walk = (nodes: readonly DesignNode[]): void => nodes.forEach((node) => {
    const componentId = node.dataBinding?.componentId;
    if (node.type === 'TEXT' && componentId) out.set(componentId, node);
    if (node.children?.length) walk(node.children);
  });
  document.pages.forEach((page) => walk(page.nodes));
  return out;
}

/** Every widget node on every page, depth-first. */
export function widgetNodes(document: DesignDocument): DesignNode[] {
  const out: DesignNode[] = [];
  const walk = (nodes: readonly DesignNode[]): void => nodes.forEach((node) => {
    if (node.type === 'WIDGET' && node.widget) out.push(node);
    if (node.children?.length) walk(node.children);
  });
  document.pages.forEach((page) => walk(page.nodes));
  return out;
}

/**
 * The node as a Layout Builder tile, or null when it was never generated.
 *
 * On the canvas the mapping sits in `build.payload`. A PUBLISHED design writes
 * it once, in `widget.binding`, so the tile takes it from there instead — the
 * two are the same object, never both.
 */
export function tileFromNode(node: DesignNode): DashboardTile | null {
  const widget = node.widget;
  const build = widget?.build;
  if (!widget || !build) return null;
  const payload = (build['payload'] ?? widget.binding ?? {}) as Record<string, unknown>;
  if (!Object.keys(payload).length) return null;
  const tile = { ...(build as unknown as DashboardTile), payload, id: node.id, layoutType: widget.layoutType };
  if (widget.filterKey) tile.filterKey = widget.filterKey;
  if (widget.listenerNodeIds?.length) tile.listenerTileIds = [...widget.listenerNodeIds];
  // Publishing strips a dropdown's picked value; start it from its "Default value".
  const control = tile.dropdownControl;
  const fallback = payload['defaultValue'];
  if (control && !control.value?.length && fallback != null && fallback !== '' && !(Array.isArray(fallback) && !fallback.length)) {
    const value = Array.isArray(fallback) ? fallback.map(String) : [String(fallback)];
    tile.dropdownControl = { ...control, value };
    tile.payload = { ...payload, value };
  }
  return withRenderData(tile, widget.data);
}

/** Put render data back on a tile; client charts also get their unfiltered rows. */
export function withRenderData(tile: DashboardTile, data: Record<string, unknown> | null | undefined): DashboardTile {
  const next = { ...tile } as unknown as Record<string, unknown>;
  const field = RENDER_FIELD[tile.layoutType];
  if (field && data) next[field] = data;
  const client = CLIENT_CHART_CROSS_FILTER[tile.layoutType];
  const rows = data?.['staticData'];
  if (client && Array.isArray(rows)) next[client.rowsField] = rows;
  return next as unknown as DashboardTile;
}

/** The `[data]` each widget renderer reads, from the tile field that family fills. */
export function tileRenderData(tile: DashboardTile): Record<string, unknown> | null {
  const field = RENDER_FIELD[tile.layoutType];
  if (field) return (tile[field] as Record<string, unknown> | null | undefined) ?? null;
  switch (tile.layoutType) {
    case 'DROPDOWN':
      return tile.dropdownControl
        ? { ...(tile.dropdownControl.widgetData ?? {}), options: tile.dropdownControl.options ?? [] }
        : null;
    case 'INPUT_BOX':
    case 'SLIDER': return (tile.inputControl?.widgetData as Record<string, unknown> | undefined) ?? null;
    case 'POPUP': return (tile.popupControl?.widgetData as Record<string, unknown> | undefined) ?? null;
    case 'FILE_UPLOAD':
      return tile.fileUploadControl
        ? { title: tile.title, buttonLabel: tile.fileUploadControl.buttonLabel, accept: tile.fileUploadControl.accept }
        : null;
    default: return null;
  }
}

/** The tile minus its rendered data, JSON-safe, for `widget.build`. */
export function storedBuild(tile: DashboardTile): Record<string, unknown> {
  const copy: Record<string, unknown> = { ...tile };
  RENDER_KEYS.forEach((key) => delete copy[key]);
  try {
    return JSON.parse(JSON.stringify(copy)) as Record<string, unknown>;
  } catch {
    const { id, componentId, layoutType, title, payload, format } = tile;
    return { id, componentId, layoutType, title, payload, format };
  }
}

/** The primary dataset a tile was built on. */
export function tileDataset(tile: DashboardTile): string {
  const payload = tile.payload ?? {};
  const datasets = Array.isArray(payload['datasets']) ? (payload['datasets'] as unknown[]).map(String) : [];
  return String(payload['dataset'] ?? datasets[0] ?? '');
}
